import { ChatBlockDisclosure, type ChatBlockContext } from "../block-disclosure";
import { DomUtils } from "@tento-ui/dom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { TermRow } from "../../term/term-row";
import {
  isRunningStatus,
  setRunningPulse,
} from "../chat-block-plugin";
import type { ChatBlockPlugin } from "../chat-block-plugin";
import type { ChatContentBlock } from "../chat-types";

/**
 * The codex dialect's block grammar.
 *
 * These two plugins are not general block kinds. They are the Codex CLI's own
 * way of drawing assistant prose and a tool call — the dim bullet, the
 * `• Ran …` line, the `└` connector — and `ChatCodexTurn.pluginTypeFor` is the
 * only thing that routes onto them. They live beside the look that declares
 * them so that `components/chat` holds only the vocabulary every look shares,
 * exactly as `claude-blocks.ts` does for the other CLI.
 *
 * The bodies below are the extraction, unchanged — only the class names, the
 * two type keys, and the sentences that said "the terminal register" where
 * they meant "the codex look" differ. The names are the substantive part: these were `TuiTextBlockPlugin` / `TuiToolCallBlockPlugin` answering
 * `text_tui` / `tool_call_tui`, and that spelling is what made a codex-specific
 * grammar read as the terminal REGISTER's for two rounds of review — including
 * to the author, who twice argued from the name that it belonged in core. The
 * register is `tui`; this grammar is `codex`; the keys now say so.
 */

/**
 * Assistant prose in the Codex CLI's own grammar:
 * a dim bullet opens the message and continuation lines hang at the
 * bullet's indent —
 *
 *     • The click producer is working: selection.ljson
 *       and selection-item.json both advanced …
 *
 * No register routes onto this on its own; the codex look's turn does, and
 * only for the assistant (the user's text stays bare — the band carries who
 * is speaking).
 */
export class CodexTextBlockPlugin implements ChatBlockPlugin<HTMLParagraphElement> {
  type = "text_codex";

  makeBlock(): HTMLParagraphElement {
    const $p = document.createElement("p");
    $p.style.margin = "0";
    $p.style.whiteSpace = "pre-wrap";
    $p.style.wordBreak = "break-word";
    $p.style.paddingLeft = "2ch";
    $p.style.textIndent = "-2ch";
    return $p;
  }

  bindBlock(
    $block: HTMLParagraphElement,
    content: ChatContentBlock,
    _isStreaming: boolean,
  ): void {
    if (content.type !== "text") return;
    $block.textContent = `• ${content.text}`;
  }

  appendDelta($block: HTMLParagraphElement, delta: string): void {
    $block.textContent += delta;
  }

  finalizeBlock(): void {}

  onRecycle($block: HTMLParagraphElement): void {
    $block.textContent = "";
  }
}

/**
 * A tool call in the Codex CLI's own grammar:
 *
 *     • Ran /Users/james/bin/tsh.dev view 14925
 *       └ interactive terminal output
 *
 * The bullet line names the invocation (shell wrapper stripped, exactly as
 * the card form's Bash(…) label strips it); the connector line carries the
 * collapsed summary — the output's first line and a dim "+N lines" — and
 * clicking the entry expands the full raw invocation and output rows under
 * the connector. Expansion state is keyed on the content-block OBJECT for
 * the same recycler reason as the card form.
 */
export class CodexToolCallBlockPlugin implements ChatBlockPlugin<HTMLElement> {
  type = "tool_call_codex";
  private expanded = new ChatBlockDisclosure();
  private state = new WeakMap<
    HTMLElement,
    {
      block: {
        tool: Option<string>;
        command: string;
        output: Option<string>;
        status: Option<string>;
      };
      $head: HTMLElement;
      $sub: HTMLElement;
      $rows: HTMLElement;
    }
  >();

  makeBlock(): HTMLElement {
    const $root = document.createElement("div");
    $root.style.whiteSpace = "var(--chat-terminal-flow, pre-wrap)";
    $root.style.wordBreak = "var(--chat-terminal-break, break-all)";

    const $head = document.createElement("div");
    $head.style.paddingLeft = "2ch";
    $head.style.textIndent = "-2ch";

    const $sub = document.createElement("div");
    $sub.style.paddingLeft = "2ch";
    $sub.style.opacity = "0.6";

    const $rows = document.createElement("div");
    $rows.style.display = "none";
    $rows.style.paddingLeft = "4ch";

    $root.append($head, $sub, $rows);

    GESTURE_MANAGER.addPointerEvent($root, {
      onClick: () => {
        const s = this.state.get($root);
        if (!s || s.block.output == null || s.block.output.length === 0) return;
        this.expanded.set(s.block, !(this.expanded.get(s.block) ?? false));
        this.render($root);
      },
      allowTextSelection: true,
    });
    return $root;
  }

  bindBlock($block: HTMLElement, content: ChatContentBlock, _streaming: boolean, context?: ChatBlockContext): void {
    if (content.type !== "tool_call") return;
    this.expanded.bind(content, context);
    const [$head, $sub, $rows] = $block.children as unknown as HTMLElement[];
    this.state.set($block, { block: content, $head, $sub, $rows });
    this.render($block);
  }

  private innerCommand(command: string): string {
    let inner = command;
    const m = inner.match(/^\s*(?:\/bin\/)?(?:zsh|bash|sh)\s+-l?c\s+([\s\S]+)$/);
    if (m) {
      inner = m[1].trim();
      const q = inner[0];
      if ((q === "'" || q === '"') && inner.length >= 2 && inner.endsWith(q)) {
        inner = inner.slice(1, -1);
      }
    }
    return inner;
  }

  private render($block: HTMLElement): void {
    const s = this.state.get($block);
    if (!s) return;
    const { block } = s;
    const outputLines =
      block.output == null || block.output.length === 0
        ? []
        : block.output.split("\n");
    const isOpen = outputLines.length > 0 && (this.expanded.get(block) ?? false);
    $block.style.cursor = outputLines.length === 0 ? "default" : "pointer";

    s.$head.textContent = `• Ran ${this.innerCommand(block.command).split("\n")[0]}`;
    setRunningPulse(s.$head, isRunningStatus(block.status));

    const failed = block.status === "failed" ? "✗ failed · " : "";
    if (isOpen) {
      s.$sub.textContent = `  └ ${failed}${outputLines.length} lines`;
      DomUtils.clearChildren(s.$rows);
      for (const raw of [...block.command.split("\n"), ...outputLines]) {
        const $row = TermRow.$row(TermRow.parse(raw));
        if (TermRow.parse(raw).text.trim().length === 0) {
          $row.style.minHeight = "1lh";
        }
        s.$rows.appendChild($row);
      }
      s.$rows.style.display = "block";
    } else {
      const preview =
        outputLines.length === 0
          ? failed || "no output"
          : `${failed}${outputLines[0]}${outputLines.length > 1 ? `  … +${outputLines.length - 1} lines` : ""}`;
      s.$sub.textContent = `  └ ${preview}`;
      DomUtils.clearChildren(s.$rows);
      s.$rows.style.display = "none";
    }
  }

  appendDelta($block: HTMLElement): void {
    // Render-only: the thread canonicalized the delta into the model, and
    // `state.block` IS the model object — appending here would double it.
    this.render($block);
  }

  finalizeBlock(): void {}

  onRecycle($block: HTMLElement): void {
    const s = this.state.get($block);
    if (s) setRunningPulse(s.$head, false);
    this.state.delete($block);
  }
}
